import React, { useState, useCallback, useEffect, useRef } from "react"
import { Tabs } from "antd"
import {
  useLocation,
  useNavigationType,
  useSearchParams,
} from "react-router-dom"

import GeneralInformationTab from "features/ProjectDetails/components/GeneralInformationTab.component"
import QuestionnaireEditorContainer from "features/QuestionnaireEditor/containers/QuestionnaireEditor.container"
import QueryConsoleContainer from "features/QueryConsole/containers/QueryConsole.container"
import DocumentsContainer from "features/Documents/containers/Documents.container"
import DataImportContainer from "features/DataImport/containers/DataImport.container"
import DataChecksContainer from "features/DataChecks/containers/DataChecks.container"
import ProvenanceTab from "features/Provenance/containers/Provenance.container"
import { DashboardsTab } from "features/Dashboard"
import { TablesTab } from "features/TabSpec"
import { TabItem } from "types/ui.types"
import { useProjectNavStore } from "stores/projectNavStore"
import { PROJECT_TYPE } from "types/project.types"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"

// Define the props interface
interface ProjectDetailsTabsProps {
  projectType: PROJECT_TYPE
  refreshProject: () => void
}

const DEFAULT_TAB = PROJECT_TAB_KEYS.GENERAL
/** The key of every tab below (some are not shown for every project, see `shown`). */
const TAB_KEYS: string[] = Object.values(PROJECT_TAB_KEYS)
/**
 * Keys a link puts in the URL for the tab it opens: `?tab=`, and with it
 * Provenance's `?section=` and the editor's `?history=`.
 */
const LINK_PARAMS = ["tab", "section", "history"]
/**
 * Project types without the Tables tab: none. A project without a
 * questionnaire builds its tables from the data, and a data-only project
 * reports "dashboard" once it has saved a dashboard, so both keep the tab.
 */
const TABLES_HIDDEN_FOR: string[] = []

/**
 * ProjectDetailsTabs component - Displays tabbed navigation for different sections of project details.
 *
 * This component leverages Ant Design's Tabs component to offer a tabbed interface, facilitating
 * the display of various sections of a project's details. Currently, it includes tabs for
 * "General" and "Questionnaire". Each tab is responsible for rendering a specific
 * component related to the project's details. The "General" tab renders the
 * GeneralInformationTab component, passing down the `refreshProject` function and the `project`
 * object as props to allow for dynamic updates. The structure is designed to be easily extendable
 * with additional tabs as needed. `?tab=<key>` in the URL opens that tab
 * first (the dashboard's "Open in console" links to the Data Reporting tab
 * this way); a key the project does not show opens the default. A later
 * change to `?tab=` while the page is open (a link from one of its tabs)
 * switches to that tab too, and Back returns to the tab shown before it.
 * The key leaves the URL once the user switches tabs.
 *
 * @component
 * @param props - The component props.
 * @returns The ProjectDetailsTabs component, rendering the tabbed interface.
 */
const ProjectDetailsTabs: React.FC<ProjectDetailsTabsProps> = ({
  projectType,
  refreshProject,
}) => {
  // Data-only and dashboard projects have no questionnaire, so hide that tab.
  // The Tables tab stays: without a questionnaire its tables come from the
  // data (TABLES_HIDDEN_FOR). Lower-cased as the old dashboard routing did,
  // in case the server's casing varies.
  const type = (projectType ?? "").toLowerCase()
  const hasNoQuestionnaire =
    type === PROJECT_TYPE.DATA_ONLY || type === PROJECT_TYPE.DASHBOARD
  // Data Checks goes with it: its checks are derived from the questionnaire's
  // routing, so without one there is nothing to check against.
  const shown = (key: string): boolean =>
    !(
      hasNoQuestionnaire &&
      (key === PROJECT_TAB_KEYS.QUESTIONNAIRE ||
        key === PROJECT_TAB_KEYS.DATA_CHECKS)
    ) &&
    !(key === PROJECT_TAB_KEYS.TABLES && TABLES_HIDDEN_FOR.includes(type))

  const [searchParams, setSearchParams] = useSearchParams()
  const [activeKey, setActiveKey] = useState<string>(() => {
    const asked = searchParams.get("tab")
    return asked && TAB_KEYS.includes(asked) && shown(asked)
      ? asked
      : DEFAULT_TAB
  })

  // Another screen of this page can ask for a tab through ?tab= (the
  // dummy-data and Provenance links do). This does not fight
  // handleTabChange: it drops ?tab= when the user picks a tab, so this sees
  // no key and leaves the user's tab alone. A link adds a history entry, so
  // Back returns to the entry before it; when that entry has no ?tab= (the
  // user picked its tab), the tab it showed comes from tabByEntry.
  const location = useLocation()
  const navigationType = useNavigationType()
  const tabByEntry = useRef(new Map<string, string>())
  useEffect(() => {
    const asked =
      searchParams.get("tab") ??
      (navigationType === "POP" ? tabByEntry.current.get(location.key) : null)
    if (asked && TAB_KEYS.includes(asked) && shown(asked)) setActiveKey(asked)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])
  // The tab each history entry shows. After the effect above, which reads
  // an entry's tab before this records the one still showing.
  useEffect(() => {
    tabByEntry.current.set(location.key, activeKey)
  }, [location.key, activeKey])

  const allItems: TabItem[] = [
    {
      key: PROJECT_TAB_KEYS.GENERAL,
      label: "General",
      children: <GeneralInformationTab refreshProject={refreshProject} />,
    },
    {
      key: PROJECT_TAB_KEYS.QUESTIONNAIRE,
      label: "Questionnaire",
      children:
        activeKey === PROJECT_TAB_KEYS.QUESTIONNAIRE ? (
          <QuestionnaireEditorContainer />
        ) : null,
    },
    {
      key: PROJECT_TAB_KEYS.DOCUMENTS,
      label: "Documents",
      children: <DocumentsContainer />,
    },
    {
      key: PROJECT_TAB_KEYS.DATA_IMPORT,
      label: "Data Import",
      // Without a questionnaire there is nothing to make dummy data from.
      children: <DataImportContainer dataOnly={hasNoQuestionnaire} />,
    },
    {
      key: PROJECT_TAB_KEYS.DATA_CHECKS,
      label: "Data Checks",
      // Lazy: only mount (and run the base check) when the tab is open.
      children:
        activeKey === PROJECT_TAB_KEYS.DATA_CHECKS ? (
          <DataChecksContainer />
        ) : null,
    },
    {
      key: PROJECT_TAB_KEYS.DATA_REPORTING,
      label: "Data Reporting",
      // No remount key here: antd mounts this pane on first activation and
      // keeps it mounted. Remounting on every tab visit (the old
      // `reporting-${reportingTabKey}` key) created a fresh engine query
      // instance — a full copy of the dataset — per visit.
      children: <QueryConsoleContainer activeKey={activeKey} />,
    },
    {
      key: PROJECT_TAB_KEYS.TABLES,
      label: "Tables",
      children:
        activeKey === PROJECT_TAB_KEYS.TABLES ? (
          <TablesTab dataOnly={hasNoQuestionnaire} />
        ) : null,
    },
    {
      key: PROJECT_TAB_KEYS.DASHBOARDS,
      label: "Dashboards",
      // Without a questionnaire there is no dummy data to offer.
      children:
        activeKey === PROJECT_TAB_KEYS.DASHBOARDS ? (
          <DashboardsTab dataOnly={hasNoQuestionnaire} />
        ) : null,
    },
    {
      key: PROJECT_TAB_KEYS.PROVENANCE,
      label: "Provenance",
      // Lazy: only mount (and fetch the audit chain) when the tab is open.
      children:
        activeKey === PROJECT_TAB_KEYS.PROVENANCE ? <ProvenanceTab /> : null,
    },
  ]

  const items: TabItem[] = allItems.filter((item) => shown(item.key))

  // Once the user picks a tab, the link has done its job: `?tab=` (and
  // `?section=`, `?history=`) goes from the URL, replacing the entry rather
  // than adding one, so a reload or a copied link no longer reopens the tab
  // it named.
  const handleTabChange = useCallback(
    (key: string) => {
      setActiveKey(key)
      if (!LINK_PARAMS.some((param) => searchParams.has(param))) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          LINK_PARAMS.forEach((param) => next.delete(param))
          return next
        },
        { replace: true }
      )
    },
    [searchParams, setSearchParams]
  )

  // The sidebar lists the sections and switches them (projectNavStore); this
  // page keeps the tab logic above. A plain string, so the effect only runs
  // when the shown sections change, not on every render's new array.
  const setNav = useProjectNavStore((state) => state.setNav)
  const clearNav = useProjectNavStore((state) => state.clearNav)
  const sectionsKey = items.map((item) => `${item.key}:${item.label}`).join("|")
  useEffect(() => {
    setNav({
      sections: items.map(({ key, label }) => ({ key, label })),
      activeKey,
      select: handleTabChange,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey, activeKey, handleTabChange, setNav])
  useEffect(() => clearNav, [clearNav])

  // The tab bar itself is hidden: the sidebar is the navigation. The panes
  // keep antd's mounting (Data Reporting stays mounted once opened).
  return (
    <Tabs
      items={items}
      activeKey={activeKey}
      onChange={handleTabChange}
      renderTabBar={() => <></>}
      className="project-details-tabs bg-white rounded-xl h-full"
    />
  )
}

export default ProjectDetailsTabs
